import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  Grid,
  LinearProgress,
  Link,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { api } from "../api/axios";
import { AuthContext } from "../contexts/AuthContext";
import StatsCard from "../components/StatsCard";
import InstallUpgradeNotice from "../components/InstallUpgradeNotice";
import { EmptyState, IconTile, PageHeader, Pill, SectionCard } from "../components/ui";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { useTheme } from "@mui/material/styles";

function fmt(n) {
  return Number(n || 0).toLocaleString();
}

function PlanRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2} sx={{ py: 1.25 }}>
      <Typography variant="body2" component="dt" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography variant="body2" component="dd" sx={{ fontWeight: 600, m: 0, textAlign: "end" }}>
        {value}
      </Typography>
    </Stack>
  );
}

export default function DashboardOverview() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const { user } = useContext(AuthContext) || {};
  const theme = useTheme();
  const ArrowIcon = theme.direction === "rtl" ? ArrowBackRoundedIcon : ArrowForwardRoundedIcon;

  const expiringSoon = useMemo(() => {
    if (!data?.subscription?.ends_at) return false;
    const ends = new Date(data.subscription.ends_at);
    const now = new Date();
    const diff = ends.getTime() - now.getTime();
    const days = diff / (1000 * 60 * 60 * 24);
    return days > 0 && days <= 7;
  }, [data]);

  const isExpired = useMemo(() => {
    if (!data?.subscription?.ends_at) return false;
    return new Date(data.subscription.ends_at) < new Date();
  }, [data]);

  useEffect(() => {
    api
      .get("/api/usage/overview")
      .then(({ data: d }) => setData(d))
      .catch((err) => setError(err?.response?.data?.message || "שגיאה"));
  }, []);

  const planLabel = useMemo(() => {
    if (!data?.plan) return "ללא מנוי פעיל";
    return `${data.plan.name}`;
  }, [data]);

  // Calculate real AI credits usage
  const aiCreditsData = useMemo(() => {
    if (!data?.plan) return null;
    const monthlyLimit = Number(data.plan.ai_credits_monthly || 0);
    const extraCredits = Number(data.entitlements?.extra_ai_credits_monthly || 0);
    const totalLimit = monthlyLimit + extraCredits;

    if (totalLimit === 0) return null;

    // For now, we don't have usage tracking - show as placeholder
    return {
      total: totalLimit,
      used: 0,
      percentage: 0,
    };
  }, [data]);

  const perSiteQuota = Number(data?.plan?.pageviews_included_monthly || 0);
  const firstName = String(user?.name || "").trim().split(/\s+/)[0];

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <InstallUpgradeNotice />

      <PageHeader
        eyebrow={firstName ? `שלום, ${firstName}` : undefined}
        title="סקירה כללית"
        subtitle="מערכת הנגישות והפרטיות שלך במבט אחד"
        actions={
          <>
            <Button variant="outlined" component={RouterLink} to="/usage" startIcon={<BarChartRoundedIcon />}>
              צפייה בשימוש מפורט
            </Button>
            <Button variant="contained" component={RouterLink} to="/sites" startIcon={<AddRoundedIcon />}>
              הוסף אתר חדש
            </Button>
          </>
        }
      />

      {isExpired && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button component={RouterLink} to="/settings" size="small" variant="contained" color="error">
              חדש עכשיו
            </Button>
          }
        >
          <AlertTitle sx={{ fontWeight: 700 }}>המנוי שלך פג תוקף</AlertTitle>
          שירותי הנגישות והפרטיות מושבתים זמנית. אנא חדש את המנוי כדי להמשיך להשתמש בשירות.
        </Alert>
      )}

      {!isExpired && expiringSoon && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button component={RouterLink} to="/settings" size="small" variant="outlined" color="inherit">
              לחידוש המנוי
            </Button>
          }
        >
          <AlertTitle sx={{ fontWeight: 700 }}>המנוי שלך מסתיים בקרוב</AlertTitle>
          נשארו פחות מ-7 ימים לסיום המנוי. מומלץ לחדש כעת כדי למנוע השבתה של השירות.
        </Alert>
      )}

      {/* KPI row */}
      <Box component="section" aria-label="מדדים עיקריים" sx={{ mb: 3 }}>
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6} lg={3}>
            <StatsCard
              icon={<LanguageOutlinedIcon />}
              tone="primary"
              label="אתרים מנוהלים"
              value={data ? fmt(data.totals?.sites_count || 0) : "..."}
              subtitle={data?.plan ? planLabel : "ללא מנוי"}
              loading={!data}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatsCard
              icon={<BarChartRoundedIcon />}
              tone="violet"
              label="טעינות החודש"
              value={data ? fmt(data.totals?.total_widget_config_hits || 0) : "..."}
              subtitle={data ? `חודש ${data.ym}` : ""}
              loading={!data}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatsCard
              icon={<VerifiedOutlinedIcon />}
              tone={data?.plan ? "success" : "warning"}
              label="סטטוס מנוי"
              value={data?.plan ? "פעיל" : "לא פעיל"}
              subtitle={data?.plan ? data.plan.name : "לרכישת חבילה"}
              loading={!data}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatsCard
              icon={<BoltOutlinedIcon />}
              tone="warning"
              label="AI Credits"
              value={aiCreditsData ? fmt(aiCreditsData.total) : data?.plan ? "0" : "לא כלול"}
              subtitle={aiCreditsData ? "זמין לשימוש" : data?.plan ? "אין במנוי" : ""}
              loading={!data}
            />
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={2.5}>
        {/* Managed Sites Table */}
        <Grid item xs={12} lg={8}>
          <SectionCard
            title="האתרים שלך"
            description="סטטוס ושימוש החודש לכל אתר"
            icon={<LanguageOutlinedIcon />}
            sx={{ height: "100%" }}
            contentSx={{ px: 0, pb: 1 }}
            action={
              <Button component={RouterLink} to="/sites" size="small" endIcon={<ArrowIcon />}>
                כל האתרים
              </Button>
            }
          >
            {!data ? (
              <Stack spacing={1.5} sx={{ px: 3, pb: 2 }}>
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} variant="rounded" height={44} />
                ))}
              </Stack>
            ) : data.sites && data.sites.length > 0 ? (
              <TableContainer>
                <Table sx={{ "& td, & th": { px: 3 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>אתר</TableCell>
                      <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>סטטוס</TableCell>
                      <TableCell align="right">טעינות</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.sites.slice(0, 5).map((site) => {
                      const hits = Number(site.widget_config_hits || 0);
                      const pct = perSiteQuota > 0 ? Math.min(100, (hits / perSiteQuota) * 100) : 0;
                      return (
                        <TableRow key={site.site_id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                          <TableCell>
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                              <IconTile size={34} tone="neutral" variant="outline">
                                <LanguageOutlinedIcon />
                              </IconTile>
                              <Box sx={{ minWidth: 0 }}>
                                <Link
                                  component={RouterLink}
                                  to={`/sites/${site.site_id}`}
                                  underline="hover"
                                  sx={{ fontWeight: 600, fontSize: "0.9rem", color: "text.primary", display: "block" }}
                                  noWrap
                                >
                                  {site.name || "ללא שם"}
                                </Link>
                                <Typography variant="caption" component="div" noWrap>
                                  {site.domain || "—"}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                            <Pill tone={site.status === "active" ? "success" : "neutral"}>
                              <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor" }} />
                              {site.status === "active" ? "פעיל" : site.status}
                            </Pill>
                          </TableCell>
                          <TableCell align="right" sx={{ width: { sm: 180 } }}>
                            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>{fmt(hits)}</Typography>
                            {perSiteQuota > 0 ? (
                              <LinearProgress
                                variant="determinate"
                                value={pct}
                                color={pct >= 90 ? "error" : pct >= 70 ? "warning" : "primary"}
                                aria-label={`ניצול מכסה ${Math.round(pct)}%`}
                                sx={{ mt: 0.75, height: 6 }}
                              />
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <EmptyState
                icon={<LanguageOutlinedIcon />}
                title="אין אתרים עדיין"
                description="הוסיפו את האתר הראשון כדי להתחיל להציג את רכיב הנגישות ומנגנון העוגיות."
                action={
                  <Button component={RouterLink} to="/sites" variant="contained" startIcon={<AddRoundedIcon />}>
                    הוסף אתר ראשון
                  </Button>
                }
              />
            )}
          </SectionCard>
        </Grid>

        {/* Plan Info */}
        <Grid item xs={12} lg={4}>
          <SectionCard
            title="החבילה שלך"
            icon={<WorkspacePremiumOutlinedIcon />}
            iconTone="violet"
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            contentSx={{ flex: 1, display: "flex", flexDirection: "column" }}
            action={data?.plan ? <Pill tone="success">פעיל</Pill> : null}
          >
            {!data ? (
              <Stack spacing={1.5}>
                <Skeleton variant="rounded" height={28} width="60%" />
                <Skeleton variant="rounded" height={120} />
              </Stack>
            ) : data.plan ? (
              <>
                <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, letterSpacing: "-0.01em" }}>{data.plan.name}</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
                  חיוב {data.plan.interval === "yearly" ? "שנתי" : "חודשי"}
                </Typography>
                <Box component="dl" sx={{ m: 0, "& > div + div": { borderTop: 1, borderColor: "divider" } }}>
                  <PlanRow label="מחזור" value={data.plan.interval === "yearly" ? "שנתי" : "חודשי"} />
                  <PlanRow label="מכסת אתרים" value={`עד ${data.plan.limits_sites} אתרים`} />
                  {perSiteQuota > 0 && <PlanRow label="מכסת טעינות" value={`${fmt(perSiteQuota)} לאתר/חודש`} />}
                  {Number(data.plan.ai_credits_monthly || 0) > 0 && (
                    <PlanRow label="AI Credits" value={`${fmt(data.plan.ai_credits_monthly)} לחודש`} />
                  )}
                </Box>
                <Box sx={{ flex: 1 }} />
                <Button component={RouterLink} to="/plans" variant="outlined" fullWidth sx={{ mt: 2.5 }}>
                  שדרג חבילה
                </Button>
              </>
            ) : (
              <EmptyState
                sx={{ py: 3 }}
                icon={<ShoppingBagOutlinedIcon />}
                title="אין לך מנוי פעיל כרגע"
                action={
                  <Button component={RouterLink} to="/plans" variant="contained" fullWidth>
                    רכוש חבילה
                  </Button>
                }
              />
            )}
          </SectionCard>
        </Grid>

        {/* Usage Overview */}
        {data?.plan && perSiteQuota > 0 && (
          <Grid item xs={12}>
            <SectionCard
              title={`צריכת משאבים · ${data.ym}`}
              icon={<BarChartRoundedIcon />}
              iconTone="violet"
              action={
                <Button component={RouterLink} to="/usage" size="small" endIcon={<ArrowIcon />}>
                  צפייה בפירוט מלא
                </Button>
              }
            >
              <Stack direction={{ xs: "column", sm: "row" }} divider={<Divider flexItem orientation="vertical" />} spacing={{ xs: 2, sm: 4 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
                    טעינות Widget החודש
                  </Typography>
                  <Typography sx={{ fontSize: "1.9rem", fontWeight: 800, letterSpacing: "-0.02em" }}>
                    {fmt(data.totals?.total_widget_config_hits || 0)}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
                    מכסה לאתר
                  </Typography>
                  <Typography sx={{ fontSize: "1.9rem", fontWeight: 800, letterSpacing: "-0.02em" }}>{fmt(perSiteQuota)}</Typography>
                </Box>
              </Stack>
            </SectionCard>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
